import {
  Users,
  MapPin,
  Truck,
  ClipboardList,
  Package,
  IndianRupee,
  FileText,
  UserCog,
  BarChart3,
  CalendarDays,
  PackageCheck,
  PackageOpen,
  FileWarning,
  Wallet,
  BadgeCheck,
  Boxes,
  Building2,
  Ban,
  HandCoins,
  Receipt,
} from 'lucide-react';
import {
  hasAttendanceAccess,
  hasExternalCommissionAccess,
  hasInternalCommissionDashboardAccess,
  hasMlAgentCommissionAccess,
} from '../../utils/networkMode.js';

/** First and last day of the current calendar month (YYYY-MM-DD). */
export function getCurrentMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const from_date = `${y}-${pad(m + 1)}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const to_date = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
  return { from_date, to_date };
}

export function getTodayIso() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function roleDefaultHubIds(user) {
  const role = user?.role_name || user?.role;
  if (role !== 'Agent' && role !== 'Hub Admin') return [];
  if (user?.location_hub_id == null || user?.location_hub_id === '') return [];
  return [String(user.location_hub_id)];
}

/** Default filters: date range empty = all dates; daily reports default to today. */
export function getDefaultFilters(def, user) {
  const base = { ...(def?.defaultFilters || {}) };
  if ('location_hub_ids' in base) {
    if (base.include_child_branches === undefined) base.include_child_branches = false;
    if (!Array.isArray(base.child_location_hub_ids)) base.child_location_hub_ids = [];
  }
  if (def?.type === 'daily-branch' || def?.type === 'receivable') {
    if (!base.report_date) base.report_date = getTodayIso();
  }
  if (def?.type === 'daily-business') {
    const today = getTodayIso();
    if (!base.from_date) base.from_date = today;
    if (!base.to_date) base.to_date = today;
  }
  if (def?.type === 'daily-branch') {
    const ids = roleDefaultHubIds(user);
    if (ids.length) base.location_hub_ids = ids;
  }
  return base;
}

/** Ordered MIS hub sections — matches client report groupings. */
export const REPORT_CATEGORIES = [
  {
    id: 'statements',
    title: 'Daily & Statements',
    description: 'Day-wise business and branch totals',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Booking, dispatch, receive, delivery and stock',
  },
  {
    id: 'accounting',
    title: 'Accounting & Payables',
    description: 'Receivables, payables, expenses and commissions',
  },
  {
    id: 'masters',
    title: 'Masters',
    description: 'Users, hubs and fleet reference reports',
  },
  {
    id: 'contracts',
    title: 'Contracts',
    description: 'On-contract and trip / vehicle contracts',
  },
  {
    id: 'hr',
    title: 'HR & Payroll',
    description: 'Attendance and salary reports',
  },
];

/** Card / detail config for each report type. */
export const REPORT_DEFS = {
  user: {
    type: 'user',
    title: 'User Report',
    description: 'Users and their booked shipments by branch and date',
    icon: Users,
    color: 'indigo',
    category: 'masters',
    feature: 'shipment',
    defaultFilters: { location_hub_ids: [], user_id: '', from_date: '', to_date: '' },
  },
  location: {
    type: 'location',
    title: 'Branch Report',
    description: 'Branch-wise GC count, other charges, paid, to-pay and on account for the booking dates',
    icon: MapPin,
    color: 'teal',
    category: 'statements',
    feature: 'shipment',
    hidden: true,
    defaultFilters: {
      location_hub_ids: [],
      from_station_ids: [],
      to_station_ids: [],
      from_date: '',
      to_date: '',
    },
  },
  vehicle: {
    type: 'vehicle',
    title: 'Vehicle Report',
    description: 'Fleet list with shipment count and amount carried on each vehicle',
    icon: Truck,
    color: 'yellow',
    category: 'masters',
    feature: 'shipment',
    defaultFilters: { vehicle_id: '', from_date: '', to_date: '' },
  },
  'ml-sheet': {
    type: 'ml-sheet',
    path: 'dispatch',
    title: 'Dispatch / OGCL Report',
    description: 'Outgoing consignment list with sender/receiver location breakdown',
    icon: ClipboardList,
    color: 'blue',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      ml_sheet_id: '',
      sender_locations: [],
      receiver_locations: [],
      from_date: '',
      to_date: '',
    },
  },
  shipment: {
    type: 'shipment',
    title: 'Shipment Report',
    description: 'Shipments with payment totals, missing & returned sheets',
    icon: Package,
    color: 'orange',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      user_id: '',
      vehicle_id: '',
      shipment_client_ids: [],
      shipment_id: '',
      gst_number: '',
      sender_contact: '',
      receiver_contact: '',
      status: [],
      sender_locations: [],
      receiver_locations: [],
      location_hub_ids: [],
      payment_modes: [],
      from_date: '',
      to_date: '',
    },
  },
  business: {
    type: 'business',
    title: 'Business Report',
    description: 'GC-wise trip, charges, paid / to-pay / on account, and commission',
    icon: FileText,
    color: 'teal',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      user_id: '',
      vehicle_id: '',
      shipment_client_ids: [],
      shipment_id: '',
      gst_number: '',
      sender_contact: '',
      receiver_contact: '',
      status: [],
      sender_locations: [],
      receiver_locations: [],
      from_station_ids: [],
      to_station_ids: [],
      location_hub_ids: [],
      payment_modes: [],
      from_date: '',
      to_date: '',
    },
  },
  'daily-business': {
    type: 'daily-business',
    title: 'Daily Business Report',
    description: 'Branch booking, on-account clients and delivered freight for the selected dates',
    icon: CalendarDays,
    color: 'blue',
    category: 'statements',
    feature: 'shipment',
    layout: 'statement',
    defaultFilters: {
      location_hub_ids: [],
      from_station_ids: [],
      to_station_ids: [],
      from_date: '',
      to_date: '',
      user_id: '',
    },
  },
  'daily-branch': {
    type: 'daily-branch',
    title: 'Daily Branch Report',
    description: 'Own company, booking agent, or delivery agent day sheet with outstanding formula',
    icon: CalendarDays,
    color: 'teal',
    category: 'statements',
    feature: 'shipment',
    defaultFilters: { report_date: '', from_date: '', to_date: '', location_hub_ids: [], user_id: '', report_side: 'both' },
  },
  'route-pl': {
    type: 'route-pl',
    title: 'Profit & Loss Report',
    description: 'Location Hub revenue, GC count and commission for a date range',
    icon: BarChart3,
    color: 'purple',
    category: 'statements',
    feature: 'shipment',
    layout: 'table',
    defaultFilters: { from_date: '', to_date: '', location_hub_ids: [] },
  },
  receive: {
    type: 'receive',
    title: 'Receive Report',
    description: 'Consignments received at destination hubs',
    icon: PackageOpen,
    color: 'teal',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      from_date: '', to_date: '', shipment_id: '',
      location_hub_ids: [], shipment_client_ids: [],
    },
  },
  'delivery-event': {
    type: 'delivery-event',
    title: 'Delivery Report',
    description: 'Delivered consignments with delivery timestamps',
    icon: PackageCheck,
    color: 'teal',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      from_date: '', to_date: '', shipment_id: '',
      location_hub_ids: [], shipment_client_ids: [],
    },
  },
  ewaybills: {
    type: 'ewaybills',
    title: 'Ewaybills Report',
    description: 'E-way bills by GC, status, vehicle and cancel reason',
    icon: FileWarning,
    color: 'yellow',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: { from_date: '', to_date: '', location_hub_ids: [], shipment_client_ids: [] },
  },
  receivable: {
    type: 'receivable',
    title: 'Receivable Report',
    description: 'Business receivables statement — branches and credit parties (Admin / Accountant)',
    icon: Wallet,
    color: 'orange',
    category: 'accounting',
    feature: 'shipment',
    layout: 'statement',
    defaultFilters: { report_date: '', location_hub_ids: [] },
  },
  'approved-transactions': {
    type: 'approved-transactions',
    title: 'Approved Transactions Report',
    description: 'Approved and paid expense transactions for branch and accounts',
    icon: BadgeCheck,
    color: 'rose',
    category: 'accounting',
    feature: 'expenses',
    defaultFilters: { location_hub_ids: [], category: '', from_date: '', to_date: '' },
  },
  'goods-balance': {
    type: 'goods-balance',
    title: 'Goods Balance Report',
    description: 'Undispatched, in-transit and pending-delivery stock by hub',
    icon: Boxes,
    color: 'indigo',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: { location_hub_ids: [] },
  },
  'onaccount-credit': {
    type: 'onaccount-credit',
    title: 'Onaccount Credit Report',
    description: 'Party-wise credit outstanding',
    icon: Building2,
    color: 'amber',
    category: 'accounting',
    feature: 'shipment',
    defaultFilters: { from_date: '', to_date: '', location_hub_ids: [], shipment_client_ids: [] },
  },
  'party-booking': {
    type: 'party-booking',
    title: 'Credit Party Report',
    description: 'Bookings grouped by shipment client / credit party',
    icon: Building2,
    color: 'blue',
    category: 'operations',
    feature: 'shipment',
    defaultFilters: {
      shipment_client_ids: [], location_hub_ids: [], from_date: '', to_date: '',
    },
  },
  'invoice-cancellation': {
    type: 'invoice-cancellation',
    title: 'Invoice Cancellation Report',
    description: 'Cancelled / rejected GCs with reason and timestamp',
    icon: Ban,
    color: 'rose',
    category: 'accounting',
    feature: 'shipment',
    defaultFilters: { from_date: '', to_date: '', location_hub_ids: [], shipment_client_ids: [] },
  },
  'branch-payable': {
    type: 'branch-payable',
    title: 'Branch Payable Report',
    description: 'Unpaid booking/delivery commission and truck hire by branch',
    icon: HandCoins,
    color: 'orange',
    category: 'accounting',
    feature: 'shipment',
    defaultFilters: { location_hub_ids: [] },
  },
  'hub-branch-agents': {
    type: 'hub-branch-agents',
    title: 'Branch Agents Report',
    description: 'Per-agent booked vs delivered GCs with paid / to-pay / credit and collected cash',
    icon: Users,
    color: 'teal',
    category: 'operations',
    feature: 'shipment',
    roles: ['Hub Admin', 'Admin', 'Accountant'],
    defaultFilters: { from_date: '', to_date: '', location_hub_ids: [] },
  },
  expense: {
    type: 'expense',
    title: 'Expense Report',
    description: 'All company expenses filtered by date range and branch',
    icon: IndianRupee,
    color: 'rose',
    category: 'accounting',
    feature: 'expenses',
    defaultFilters: { location_hub_ids: [], category: '', from_date: '', to_date: '' },
  },
  'on-contract': {
    type: 'on-contract',
    title: 'On-Contract Report',
    description: 'Contract-wise P&L with ML sheet and shipment breakdown',
    icon: FileText,
    color: 'purple',
    category: 'contracts',
    feature: 'on_contract',
    defaultFilters: { contract_id: '', vehicle_id: '', from_date: '', to_date: '' },
  },
  'vehicle-contract': {
    type: 'vehicle-contract',
    title: 'Vehicle Contract Report',
    description: 'Fleet contracts with daily trips and payment history',
    icon: Truck,
    color: 'blue',
    category: 'contracts',
    feature: 'on_contract_truck',
    defaultFilters: { contract_id: '', vehicle_id: '', from_date: '', to_date: '' },
  },
  'trip-contract': {
    type: 'trip-contract',
    title: 'Trip Contract Report',
    description: 'Trip contracts with trip details and payment tracking',
    icon: Package,
    color: 'indigo',
    category: 'contracts',
    feature: 'on_contract_truck',
    defaultFilters: { contract_id: '', vehicle_id: '', from_date: '', to_date: '' },
  },
  'agent-tds': {
    type: 'agent-tds',
    title: 'TDS Report',
    description: 'Daily TDS deducted for each agent — Section 194H, PAN, commission and TDS amount',
    icon: HandCoins,
    color: 'orange',
    category: 'accounting',
    feature: 'shipment',
    defaultFilters: {
      agent_user_id: '',
      user_id: '',
      location_hub_ids: [],
      from_date: '',
      to_date: '',
    },
  },
  'gst-collection': {
    type: 'gst-collection',
    title: 'GST Collection Report',
    description: 'State-wise GST collection for bookings and cancels. Admin and Accountant only.',
    icon: Receipt,
    color: 'teal',
    category: 'accounting',
    feature: 'shipment',
    roles: ['Admin', 'Accountant', 'System Admin'],
    layout: 'statement',
    defaultFilters: {
      from_date: '',
      to_date: '',
      collection_type: 'booking',
      gst_filter: 'gst',
    },
  },
  commission: {
    type: 'commission',
    title: 'Agent Commission Report',
    description: 'Booking and delivery agent commission — same data as Settlements → Book & Delivery Comm.',
    icon: IndianRupee,
    color: 'orange',
    category: 'accounting',
    access: 'ml_commission',
    defaultFilters: {
      agent_user_id: '',
      location_hub_ids: [],
      contract_party_id: '',
      status: '',
      from_date: '',
      to_date: '',
    },
  },
  'external-commission': {
    type: 'external-commission',
    title: 'Shipment Commission',
    description: 'Bulk agent settlement report — EXTERNAL network',
    icon: IndianRupee,
    color: 'amber',
    category: 'accounting',
    access: 'external_commission',
    defaultFilters: {
      vehicle_owner_id: '',
      agent_user_ids: [],
      from_date: '',
      to_date: '',
    },
  },
  hr: {
    type: 'hr',
    title: 'HR Report',
    description: 'Attendance and salary records with full filter support',
    icon: UserCog,
    color: 'teal',
    category: 'hr',
    access: 'attendance',
    defaultFilters: {
      type: 'both',
      user_id: '',
      from_date: '',
      to_date: '',
      month: '',
      year: '',
      status: '',
      payment_mode: '',
      city: '',
      state: '',
    },
  },
};

/** Flat list for company-settings report permission checkboxes. */
export const REPORT_TYPE_OPTIONS = Object.values(REPORT_DEFS).filter((d) => !d.roles).map((d) => {
  const category = REPORT_CATEGORIES.find((c) => c.id === (d.category || 'operations'));
  return {
    type: d.type,
    title: d.title,
    description: d.description || '',
    category: d.category || 'operations',
    categoryTitle: category?.title || 'Reports',
  };
});

export const colorMap = {
  indigo: {
    topBar: '#c7d2fe', iconBg: '#e0e7ff', iconColor: '#4f46e5',
    buttonBg: '#eef2ff', buttonText: '#4338ca', buttonBorder: '#c7d2fe', shadow: 'rgba(99,102,241,0.10)',
  },
  teal: {
    topBar: '#99f6e4', iconBg: '#ccfbf1', iconColor: '#0d9488',
    buttonBg: '#f0fdfa', buttonText: '#0f766e', buttonBorder: '#99f6e4', shadow: 'rgba(20,184,166,0.10)',
  },
  yellow: {
    topBar: '#fde68a', iconBg: '#fef3c7', iconColor: '#d97706',
    buttonBg: '#fffbeb', buttonText: '#b45309', buttonBorder: '#fde68a', shadow: 'rgba(245,158,11,0.10)',
  },
  blue: {
    topBar: '#bfdbfe', iconBg: '#dbeafe', iconColor: '#2563eb',
    buttonBg: '#eff6ff', buttonText: '#1d4ed8', buttonBorder: '#bfdbfe', shadow: 'rgba(37,99,235,0.10)',
  },
  orange: {
    topBar: '#fed7aa', iconBg: '#ffedd5', iconColor: '#ea580c',
    buttonBg: '#fff7ed', buttonText: '#c2410c', buttonBorder: '#fed7aa', shadow: 'rgba(234,88,12,0.10)',
  },
  rose: {
    topBar: '#fecdd3', iconBg: '#ffe4e6', iconColor: '#e11d48',
    buttonBg: '#fff1f2', buttonText: '#be123c', buttonBorder: '#fecdd3', shadow: 'rgba(225,29,72,0.10)',
  },
  purple: {
    topBar: '#e9d5ff', iconBg: '#f3e8ff', iconColor: '#9333ea',
    buttonBg: '#faf5ff', buttonText: '#7e22ce', buttonBorder: '#e9d5ff', shadow: 'rgba(147,51,234,0.10)',
  },
  amber: {
    topBar: '#fde68a', iconBg: '#fef3c7', iconColor: '#d97706',
    buttonBg: '#fffbeb', buttonText: '#b45309', buttonBorder: '#fde68a', shadow: 'rgba(245,158,11,0.10)',
  },
};

export const AGENT_MIS_TYPES = new Set([
  'shipment',
  'business',
  'location',
  'ml-sheet',
  'receive',
  'delivery-event',
  'ewaybills',
  'goods-balance',
  'party-booking',
  'invoice-cancellation',
  'expense',
  'commission',
  'daily-branch',
  'agent-tds',
  'hr',
]);

export function canExportExcel(user) {
  const role = user?.role_name || user?.role;
  return ['System Admin', 'Admin', 'Owner', 'Accountant', 'Hub Admin', 'Agent'].includes(role);
}

export const ALL_REPORT_TYPES = Object.values(REPORT_DEFS)
  .filter((d) => !d.roles)
  .map((d) => d.type);

export const ALWAYS_ALL_REPORT_ROLES = new Set(['System Admin', 'Admin', 'Owner', 'Accountant']);

export function defaultReportTypesForRole(role) {
  if (ALWAYS_ALL_REPORT_ROLES.has(role)) return [...ALL_REPORT_TYPES];
  if (role === 'Agent') return [...AGENT_MIS_TYPES];
  if (role === 'Hub Admin') return [...ALL_REPORT_TYPES];
  return [];
}

export function isReportVisible(def, user) {
  if (!user || !def) return false;
  const role = user.role_name || user.role;
  const isSystemAdmin = role === 'System Admin';
  const ca = user.company_features || user.company_can_access || {};

  if (Array.isArray(user.allowed_report_types)) {
    if (!user.allowed_report_types.includes(def.type)) return false;
  } else {
    if (role === 'Agent' && !AGENT_MIS_TYPES.has(def.type)) return false;
    if (def.roles && role !== 'System Admin' && !def.roles.includes(role)) return false;
  }

  if (def.access === 'ml_commission') {
    if (role === 'Agent' || role === 'Hub Admin') {
      return hasInternalCommissionDashboardAccess(user);
    }
    return hasMlAgentCommissionAccess(user);
  }
  if (def.access === 'external_commission') return hasExternalCommissionAccess(user);
  if (def.access === 'attendance') return hasAttendanceAccess(user);
  // Feature-gated cards (e.g. shipment) show when company.can_access.<feature> is true
  if (def.feature) return isSystemAdmin || Boolean(ca[def.feature]);
  return isSystemAdmin;
}

export function listVisibleReports(user) {
  return Object.values(REPORT_DEFS).filter((d) => !d.hidden && isReportVisible(d, user));
}

/** Visible reports grouped by REPORT_CATEGORIES order (empty categories omitted). */
export function listVisibleReportsByCategory(user) {
  const visible = listVisibleReports(user);
  const byCat = {};
  visible.forEach((def) => {
    const key = def.category || 'operations';
    if (!byCat[key]) byCat[key] = [];
    byCat[key].push(def);
  });
  return REPORT_CATEGORIES
    .map((cat) => ({
      ...cat,
      reports: byCat[cat.id] || [],
    }))
    .filter((cat) => cat.reports.length > 0);
}

export function buildCleanParams(filterObj = {}) {
  const p = {};
  Object.entries(filterObj).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      if (v.length > 0) p[k] = v;
    } else if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed !== '') p[k] = trimmed;
    } else if (v !== '' && v !== null && v !== undefined) {
      p[k] = v;
    }
  });
  return p;
}
